import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import { useLang } from "../context/LangContext";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export default function SearchBar({ big = false }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [transaction, setTransaction] = useState("sale");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    const id = setTimeout(() => {
      api.get("/search/autocomplete", { params: { q } }).then((r) => setSuggestions(r.data.items));
    }, 200);
    return () => clearTimeout(id);
  }, [q]);

  const submit = (e, override) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    params.set("transaction", transaction);
    const sel = override || (suggestions[0] && q ? suggestions[0] : null);
    if (sel) {
      if (sel.wilaya) params.set("wilaya", sel.wilaya);
      if (sel.city) params.set("city", sel.city);
    } else if (q) params.set("q", q);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <form onSubmit={submit} className={`bg-white rounded-2xl shadow-2xl ${big ? "p-3 md:p-4" : "p-3"} flex flex-col md:flex-row gap-3 items-stretch relative`} data-testid="search-bar">
      <div className="flex rounded-xl bg-slate-50 p-1 shrink-0">
        <button type="button" onClick={() => setTransaction("sale")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${transaction === "sale" ? "bg-white text-[#1B8F5A] shadow-sm" : "text-slate-500"}`} data-testid="tx-sale">{t("buy")}</button>
        <button type="button" onClick={() => setTransaction("rent")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${transaction === "rent" ? "bg-white text-[#1B8F5A] shadow-sm" : "text-slate-500"}`} data-testid="tx-rent">{t("rent")}</button>
      </div>

      <div className="flex-1 relative">
        <div className="flex items-center gap-2 h-12 px-4 rounded-xl bg-slate-50 border border-slate-200">
          <MapPin size={16} className="text-slate-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 200)}
            placeholder={t("autocompleteHint")}
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="search-input"
          />
        </div>
        {showSug && suggestions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 max-h-72 overflow-auto" data-testid="autocomplete-list">
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => submit(null, s)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-2 text-sm">
                <MapPin size={14} className="text-slate-400" />
                <span>{s.label}</span>
                <span className="text-xs text-slate-400 ml-auto">{s.type}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Button type="submit" className="h-12 px-7 bg-[#1B8F5A] hover:bg-[#157047] rounded-xl text-base font-semibold" data-testid="search-submit">
        <Search size={17} className="mr-2" />{t("search")}
      </Button>
    </form>
  );
}
